/**
 * quoteHeading — o SUBTÍTULO e o LOCAL DE EMISSÃO do documento.
 *
 * ─── POR QUE OS DOIS MORAM NO MESMO MÓDULO ──────────────────────────────────
 *
 * São as duas colunas de CABEÇALHO que nasceram juntas (lacunas L6/L7 da
 * análise do concorrente) e compartilham a regra que define as duas: **ausência
 * repete o passado, byte a byte**. Um documento sem subtítulo e sem local sai
 * exatamente como saía antes de as colunas existirem — o mesmo contrato do
 * título (#202), da garantia (#104) e do QR PIX (#73).
 *
 * Separá-las em dois módulos duplicaria o par builder/leitor e os ~13 payloads
 * de `quotes` passariam a cobrar duas chamadas em vez de uma, dobrando a chance
 * de um site levar uma e esquecer a outra — que é exatamente o modo de falha
 * das armadilhas #33/#62/#70.
 *
 * ─── SUBTÍTULO ──────────────────────────────────────────────────────────────
 *
 * Linha secundária sob o cabeçalho ("Manutenção de celulares"). Ele existe
 * porque o #202 fez o TÍTULO substituir a palavra do tipo: quem batiza o
 * documento perde o "ORÇAMENTO" do cabeçalho. Com subtítulo dá para ter os
 * dois — título em cima, natureza do serviço embaixo — sem desfazer o #202.
 *
 * ─── LOCAL DE EMISSÃO ───────────────────────────────────────────────────────
 *
 * A cidade do "São Paulo, 9 de agosto de 2026" que todo documento brasileiro
 * carrega. O RECIBO já tinha (`receipts.issue_place`); o orçamento não tinha, e
 * é o mesmo dado com o mesmo nome de coluna de propósito.
 *
 * ⚠️ **A DATA SOZINHA NÃO É IMPRESSA.** A linha inteira só aparece quando há
 * LOCAL. Parece arbitrário e não é: a data existe em todo documento (`quoteDate`),
 * então imprimi-la sem o local acrescentaria uma linha nova em **todo PDF já
 * emitido** — o histórico mudaria de aparência porque uma coluna nasceu. Com o
 * gate no local, quem nunca preencheu continua com o documento de sempre.
 */

export interface QuoteHeading {
  /** `quotes.subtitle` — linha secundária do cabeçalho. `''` = ausente. */
  subtitle: string;
  /** `quotes.issue_place` — cidade de emissão. `''` = ausente. */
  issuePlace: string;
}

/** Texto de usuário: só espaços é ausência, nunca conteúdo (#61/#73). */
const clean = (value: unknown): string =>
  typeof value === 'string' && value.trim() ? value.trim() : '';

/**
 * Leitor: da linha do banco (ou do cache) para o formato da aplicação.
 *
 * Espalhe (`...quoteHeadingFromRow(row)`) na hidratação do formulário. A
 * armadilha #70 é a metade esquecida: se a LEITURA perde o campo, o próximo
 * save grava vazio por cima e o dado some sozinho — sem erro, sem toast.
 */
export function quoteHeadingFromRow(row: Record<string, any> | null | undefined): QuoteHeading {
  return {
    subtitle: clean(row?.subtitle),
    issuePlace: clean(row?.issue_place),
  };
}

/**
 * Gravador: as DUAS colunas de uma vez.
 *
 * Espalhe (`...buildQuoteHeadingColumns(data)`) em vez de escrever as chaves à
 * mão — são ~13 payloads de `quotes` no app, montados por lista explícita, e o
 * guard de varredura de `quoteTotals.test.ts` cobra esta chamada dentro de cada
 * literal. Extrair para uma variável e espalhar a variável DESLIGA o guard
 * (#75): mantenha a chamada repetida.
 */
export function buildQuoteHeadingColumns(
  info: Partial<QuoteHeading> | null | undefined,
): { subtitle: string | null; issue_place: string | null } {
  return {
    subtitle: clean(info?.subtitle) || null,
    issue_place: clean(info?.issuePlace) || null,
  };
}

/**
 * A linha "São Paulo, 9 de agosto de 2026" — devolve `''` quando não há local.
 *
 * `quoteDate` é coluna `DATE` ('YYYY-MM-DD'), então a data é montada à meia-noite
 * LOCAL (`new Date(\`${d}T00:00:00\`)`). `new Date(d)` puro parseia como UTC e em
 * UTC-3 imprime o DIA ANTERIOR — o erro do #114, que só apareceu num documento
 * com valor probatório.
 */
export function formatIssueLine(issuePlace: string | null | undefined, quoteDate?: string | null): string {
  const place = clean(issuePlace);
  if (!place) return '';

  const raw = typeof quoteDate === 'string' ? quoteDate.split('T')[0] : '';
  const parsed = raw ? new Date(`${raw}T00:00:00`) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return place;

  const formatted = parsed.toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return `${place}, ${formatted}`;
}
